import { Form as FormikForm } from 'formik';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  VStack,
} from '@frontend/components';

import { useFeatureFlagFormContext } from 'modules/common/drawers/featureFlagDrawer/components/FormWrapper/FeatureFlagFormContext';
import { featureFlagsStaticData } from 'modules/common/drawers/featureFlagDrawer/const/featureFlags';
import {
  FeatureFlagAvailability,
  FeatureFlagCategory,
  FeatureFlagData,
  FeatureFlagType,
} from 'modules/common/drawers/featureFlagDrawer/types';

import { FormFieldRow, FormSection } from './components';

const errorHasMessage = (error: unknown): error is { message: unknown } =>
  typeof error === 'object' && error !== null && 'message' in error;

// Define availability order for sorting within categories
const availabilityOrder = [
  FeatureFlagAvailability.CheckEntitlement, // Configurable/Entitled
  FeatureFlagAvailability.CheckInstructions, // Check instructions
  FeatureFlagAvailability.ConsultRD, // Consult R&D
  FeatureFlagAvailability.NotAvailable, // Not available
];

const getAvailabilitySortOrder = (availability: FeatureFlagAvailability) => {
  const index = availabilityOrder.indexOf(availability);
  return index === -1 ? 999 : index;
};

// Helper to check if all tiers are enabled (for boolean flags)
const areAllTiersEnabled = (flag: FeatureFlagData): boolean => {
  if (flag.type !== FeatureFlagType.Boolean) return false;
  return (
    Boolean(flag.tierInfo.growth.default) &&
    Boolean(flag.tierInfo.pro.default) &&
    Boolean(flag.tierInfo.ent.default) &&
    Boolean(flag.tierInfo.entMs.default)
  );
};

// Helper to check if all tiers are disabled (for boolean flags)
const areAllTiersDisabled = (flag: FeatureFlagData): boolean => {
  if (flag.type !== FeatureFlagType.Boolean) return false;
  return (
    !flag.tierInfo.growth.default &&
    !flag.tierInfo.pro.default &&
    !flag.tierInfo.ent.default &&
    !flag.tierInfo.entMs.default
  );
};

// Helper to check if any tier has upsell
const hasUpsell = (flag: FeatureFlagData): boolean => {
  return (
    flag.tierInfo.growth.upsell ||
    flag.tierInfo.pro.upsell ||
    flag.tierInfo.ent.upsell ||
    flag.tierInfo.entMs.upsell
  );
};

// Helper to check if flag has "as required by configuration" (any tier has undefined default)
const isAsRequiredByConfiguration = (flag: FeatureFlagData): boolean => {
  if (flag.type !== FeatureFlagType.Boolean) return false;
  return (
    flag.tierInfo.growth.default === undefined ||
    flag.tierInfo.pro.default === undefined ||
    flag.tierInfo.ent.default === undefined ||
    flag.tierInfo.entMs.default === undefined
  );
};

// Helper to check if flag has any red (disabled) tiers
const hasAnyRedTier = (flag: FeatureFlagData): boolean => {
  if (flag.type !== FeatureFlagType.Boolean) return false;
  return (
    flag.tierInfo.growth.default === false ||
    flag.tierInfo.pro.default === false ||
    flag.tierInfo.ent.default === false ||
    flag.tierInfo.entMs.default === false
  );
};

// Count number of enabled tiers
const getEnabledTierCount = (flag: FeatureFlagData): number => {
  if (flag.type !== FeatureFlagType.Boolean) return 0;
  let count = 0;
  if (flag.tierInfo.growth.default === true) count++;
  if (flag.tierInfo.pro.default === true) count++;
  if (flag.tierInfo.ent.default === true) count++;
  if (flag.tierInfo.entMs.default === true) count++;
  return count;
};

// Count number of disabled tiers (red)
const getDisabledTierCount = (flag: FeatureFlagData): number => {
  if (flag.type !== FeatureFlagType.Boolean) return 0;
  let count = 0;
  if (flag.tierInfo.growth.default === false) count++;
  if (flag.tierInfo.pro.default === false) count++;
  if (flag.tierInfo.ent.default === false) count++;
  if (flag.tierInfo.entMs.default === false) count++;
  return count;
};

// Calculate score biased towards more green in pro/ent (Enterprise=3, Pro=2, Growth=1, disabled=0)
const getProEntGreenScore = (flag: FeatureFlagData): number => {
  if (flag.type !== FeatureFlagType.Boolean) return 0;
  let score = 0;
  if (flag.tierInfo.entMs.default === true) score += 4;
  if (flag.tierInfo.ent.default === true) score += 3;
  if (flag.tierInfo.pro.default === true) score += 2;
  if (flag.tierInfo.growth.default === true) score += 1;
  return score;
};

export const Form = ({ searchTerm }: { searchTerm: string }) => {
  const {
    featureFlagsQueryResult: { isError, error },
  } = useFeatureFlagFormContext();

  const errorMessage = errorHasMessage(error) ? String(error.message) : null;

  // Define category order: Configuration first, then Entitlement, Development, Backburner
  const categoryOrder = [
    FeatureFlagCategory.Configuration,
    FeatureFlagCategory.Entitlement,
    FeatureFlagCategory.Development,
    FeatureFlagCategory.Backburner,
  ];

  // Collect categories and their fields
  const categories = categoryOrder.map((category) => {
    const data = Object.entries(featureFlagsStaticData)
      // Associated with this category
      .filter(
        ([, flag]: [string, FeatureFlagData]) => flag.category === category,
      )
      // Matching search filter, if applied
      .filter(([, flag]: [string, FeatureFlagData]) => {
        return (
          searchTerm.length < 3 ||
          flag.displayName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
      // Sort: Availability, then type (non-boolean first), then boolean sorting groups, then deprecation, then alphabetically
      .sort(
        (
          [, flagA]: [string, FeatureFlagData],
          [, flagB]: [string, FeatureFlagData],
        ) => {
          // First: Sort by availability order (CheckEntitlement, CheckInstructions, ConsultRD, NotAvailable)
          const orderA = getAvailabilitySortOrder(flagA.availability);
          const orderB = getAvailabilitySortOrder(flagB.availability);
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          // Second: Non-numbers (non-boolean) first
          const isBooleanA = flagA.type === FeatureFlagType.Boolean ? 1 : 0;
          const isBooleanB = flagB.type === FeatureFlagType.Boolean ? 1 : 0;
          if (isBooleanA !== isBooleanB) {
            return isBooleanA - isBooleanB;
          }
          // Third: For boolean flags, sort by specific groups
          if (
            flagA.type === FeatureFlagType.Boolean &&
            flagB.type === FeatureFlagType.Boolean
          ) {
            // Get group numbers for each flag (lower = higher priority)
            const getGroupNumber = (flag: FeatureFlagData): number => {
              // Group 1: No defaults
              if (isAsRequiredByConfiguration(flag)) return 1;
              // Group 2: All green (all enabled)
              if (areAllTiersEnabled(flag)) return 2;
              // Group 6: All red (all disabled)
              if (areAllTiersDisabled(flag)) return 6;
              // Groups 3-5: Any red
              if (hasAnyRedTier(flag)) {
                // Group 3: Any red, no contracted (not contracted and not upsell)
                if (!flag.contracted && !hasUpsell(flag)) return 3;
                // Group 4: Any red, contracted
                if (flag.contracted) return 4;
                // Group 5: Any red, upsell non-contracted
                if (hasUpsell(flag)) return 5;
              }
              // Default fallback
              return 7;
            };
            const groupA = getGroupNumber(flagA);
            const groupB = getGroupNumber(flagB);

            // For groups 3-5 (any red), apply green-red sorting across groups first
            if (groupA >= 3 && groupA <= 5 && groupB >= 3 && groupB <= 5) {
              // First: Flags with more green than red come before flags with more red than green
              const greenA = getEnabledTierCount(flagA);
              const redA = getDisabledTierCount(flagA);
              const greenB = getEnabledTierCount(flagB);
              const redB = getDisabledTierCount(flagB);
              const greenRedDiffA = greenA - redA;
              const greenRedDiffB = greenB - redB;
              if (greenRedDiffA !== greenRedDiffB) {
                return greenRedDiffB - greenRedDiffA; // More green than red first
              }
              // Then: More enabled tiers come first
              if (greenA !== greenB) {
                return greenB - greenA; // Higher count first
              }
              // Then: Bias towards more green in pro/ent (Enterprise > Pro > Growth)
              const scoreA = getProEntGreenScore(flagA);
              const scoreB = getProEntGreenScore(flagB);
              if (scoreA !== scoreB) {
                return scoreB - scoreA; // Higher score first
              }
              // Then: Sort by group number (3, 4, 5)
              if (groupA !== groupB) {
                return groupA - groupB;
              }
            } else {
              // For other groups, sort by group number first
              if (groupA !== groupB) {
                return groupA - groupB;
              }
            }
          }
          // Fourth: Sort by deprecation status: non-deprecated first, deprecated last
          const isDeprecatedA = flagA.deprecate ? 1 : 0;
          const isDeprecatedB = flagB.deprecate ? 1 : 0;
          if (isDeprecatedA !== isDeprecatedB) {
            return isDeprecatedA - isDeprecatedB;
          }
          // Finally, sort alphabetically by display name
          return flagA.displayName.localeCompare(flagB.displayName);
        },
      );

    return {
      label: category,
      flags: data,
    };
  });

  if (isError) {
    return (
      <Alert
        status="error"
        padding={1}
        margin={2}
        display={'flex'}
        flexDirection={'column'}
        width={'auto'}
      >
        <AlertTitle>Failed to edit feature flags</AlertTitle>
        {errorMessage && (
          <AlertDescription>{String(errorMessage)}</AlertDescription>
        )}
      </Alert>
    );
  }

  return (
    <FormikForm>
      <VStack spacing={2} align="stretch">
        {categories.map(({ label, flags }) => {
          // If there are no flags in this category, skip rendering the section
          if (flags.length === 0) {
            return null;
          }

          return (
            <FormSection name={label} category={label} key={label}>
              {flags.map(
                ([flagKey, flagData]: [string, FeatureFlagData], index) => (
                  <FormFieldRow
                    key={flagKey}
                    flagKey={flagKey}
                    flagData={flagData}
                    index={index}
                  />
                ),
              )}
            </FormSection>
          );
        })}
      </VStack>
    </FormikForm>
  );
};
